import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardList, Search, X, Loader2, RefreshCw, Check, Ban, CheckCheck, Undo2,
  ExternalLink, ChevronLeft, ChevronRight, Scale, Hand, Calendar, Trash2, FileText, Paperclip
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { expedientesAPI } from '../api';

const URGENCIAS = {
  inmediata: 'bg-red-100 text-red-800 border-red-200',
  alta: 'bg-amber-100 text-amber-900 border-amber-200',
  media: 'bg-blue-100 text-blue-800 border-blue-200',
  baja: 'bg-gray-100 text-gray-700 border-gray-200',
};

const ESTADOS = {
  propuesto: { etq: 'Propuesto', chip: 'bg-purple-100 text-purple-800 border-purple-200' },
  aprobado: { etq: 'Aprobado', chip: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  descartado: { etq: 'Descartado', chip: 'bg-gray-100 text-gray-600 border-gray-200' },
  hecho: { etq: 'Hecho', chip: 'bg-sky-100 text-sky-800 border-sky-200' },
};

const FILTROS_INICIALES = {
  estado: 'propuesto', buscar: '', urgencia: '', solo_letrado: false,
  orden: 'urgencia', pagina: 1, por_pagina: 50,
};

const clave = (e) => `${e.actuacion_id}:${e.encargo_ref}`;

const fechaCorta = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch (err) { return iso; }
};

const FilaEncargo = ({ item, seleccionado, onSeleccionar, onDecidir, ocupado }) => {
  const est = ESTADOS[item.estado] || ESTADOS.propuesto;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
      data-testid={`encargo-ia-fila-${item.actuacion_id}-${item.encargo_ref}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={seleccionado}
          onChange={() => onSeleccionar(clave(item))}
          className="mt-1 w-4 h-4 accent-blue-600 cursor-pointer"
          aria-label={`Seleccionar encargo de ${item.numero_expediente}`}
          data-testid={`encargo-ia-seleccionar-${item.actuacion_id}-${item.encargo_ref}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`/expedientes/${item.expediente_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-700 hover:text-blue-900 hover:underline flex items-center gap-1"
              data-testid={`encargo-ia-ir-expediente-${item.expediente_id}`}
            >
              {item.numero_expediente}<ExternalLink className="w-3.5 h-3.5" />
            </a>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${est.chip}`}>{est.etq}</span>
            {item.urgencia && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${URGENCIAS[(item.urgencia || '').toLowerCase()] || URGENCIAS.baja}`}>
                urgencia {item.urgencia}
              </span>
            )}
            {item.decide_letrado && (
              <span className="text-[10px] uppercase tracking-wide text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded flex items-center gap-1">
                <Scale className="w-3 h-3" />decide el letrado
              </span>
            )}
            {item.asistido && (
              <span className="text-[10px] uppercase tracking-wide text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded flex items-center gap-1">
                <Hand className="w-3 h-3" />asistido
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900 mt-1.5">{item.tipo}: {item.titulo}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />auditoría del {fechaCorta(item.fecha_auditoria)}
            {item.fecha_estado && ` · ${est.etq.toLowerCase()} el ${fechaCorta(item.fecha_estado)}`}
            {item.usuario && ` por ${item.usuario}`}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap justify-end">
        {item.estado !== 'aprobado' && (
          <button type="button" disabled={ocupado} onClick={() => onDecidir([clave(item)], 'aprobado')}
            title="Se redactará esta noche"
            className="text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid={`encargo-ia-aprobar-${item.actuacion_id}-${item.encargo_ref}`}>
            <Check className="w-4 h-4" />Aprobar
          </button>
        )}
        {item.estado !== 'descartado' && (
          <button type="button" disabled={ocupado} onClick={() => onDecidir([clave(item)], 'descartado')}
            className="text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            data-testid={`encargo-ia-descartar-${item.actuacion_id}-${item.encargo_ref}`}>
            <Ban className="w-4 h-4" />Descartar
          </button>
        )}
        {item.estado === 'aprobado' && (
          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
            data-testid={`encargo-ia-aviso-noche-${item.actuacion_id}-${item.encargo_ref}`}>
            Se redactará esta noche
          </span>
        )}
        {item.estado !== 'hecho' && (
          <button type="button" disabled={ocupado} onClick={() => onDecidir([clave(item)], 'hecho')}
            className="text-xs text-sky-700 hover:text-sky-900 flex items-center gap-1 disabled:opacity-50"
            data-testid={`encargo-ia-hecho-${item.actuacion_id}-${item.encargo_ref}`}>
            <CheckCheck className="w-3.5 h-3.5" />Hecho
          </button>
        )}
        {item.estado !== 'propuesto' && (
          <button type="button" disabled={ocupado} onClick={() => onDecidir([clave(item)], 'propuesto')}
            className="text-xs text-purple-700 hover:text-purple-900 flex items-center gap-1 disabled:opacity-50"
            data-testid={`encargo-ia-reabrir-${item.actuacion_id}-${item.encargo_ref}`}>
            <Undo2 className="w-3.5 h-3.5" />Volver a propuesto
          </button>
        )}
      </div>
    </div>
  );
};

export const EncargosIAPanel = () => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [texto, setTexto] = useState('');
  const [seleccion, setSeleccion] = useState([]);
  const temporizador = useRef(null);

  const cargar = useCallback(async (f) => {
    setCargando(true);
    setError('');
    try {
      const res = await expedientesAPI.encargosIAListar(f);
      setDatos(res.data || null);
    } catch (e) {
      setError('No se han podido cargar los encargos.');
    }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(filtros); }, [filtros, cargar]);

  useEffect(() => {
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => {
      setFiltros((f) => (f.buscar === texto ? f : { ...f, buscar: texto, pagina: 1 }));
    }, 400);
    return () => temporizador.current && clearTimeout(temporizador.current);
  }, [texto]);

  const cambiar = (cambios) => { setSeleccion([]); setFiltros((f) => ({ ...f, ...cambios, pagina: 1 })); };

  const resultados = datos?.resultados || [];
  const contadores = datos?.contadores || {};
  const total = datos?.total || 0;
  const paginas = datos?.paginas || 1;
  const todos = resultados.length > 0 && seleccion.length === resultados.length;

  const decidir = async (claves, estado) => {
    if (estado === 'descartado' && !window.confirm(`Se descartarán ${claves.length} encargo${claves.length === 1 ? '' : 's'}. El auditor no los redactará. ¿Continuar?`)) return;
    setOcupado(true);
    setAviso('');
    try {
      const res = await expedientesAPI.encargosIADecidir({ encargos: claves, estado });
      setAviso(estado === 'aprobado'
        ? `${res.data?.cambiados || 0} encargo(s) aprobado(s): se redactarán esta noche y el borrador aparecerá en el expediente con el Word adjunto.`
        : `${res.data?.cambiados || 0} encargo(s) → ${estado}. La decisión queda anotada en la auditoría del expediente.`);
      setSeleccion([]);
      await cargar(filtros);
    } catch (e) {
      setError('No se ha podido guardar la decisión.');
    }
    setOcupado(false);
  };

  const sincronizar = async () => {
    setOcupado(true);
    setAviso('');
    try {
      const res = await expedientesAPI.encargosIASincronizar(true);
      setAviso(`Revisadas las auditorías: ${res.data?.nuevos || 0} encargo(s) nuevo(s).`);
      await cargar(filtros);
    } catch (e) {
      setError('No se ha podido sincronizar.');
    }
    setOcupado(false);
  };

  return (
    <div data-testid="encargos-ia-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <p className="text-sm text-gray-500 max-w-2xl">
          Trabajos que propone el auditor en cada auditoría. Al aprobarlos, la decisión queda anotada en la
          actuación del expediente y el auditor los redacta en su siguiente pasada.
        </p>
        <Button variant="outline" size="sm" onClick={sincronizar} disabled={ocupado} data-testid="encargos-ia-sincronizar">
          {ocupado ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Releer auditorías
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-5 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por nº de expediente, cliente o tipo de escrito…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="encargos-ia-buscador"
          />
          {texto && (
            <button type="button" onClick={() => setTexto('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="encargos-ia-limpiar-busqueda">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <select value={filtros.urgencia} onChange={(e) => cambiar({ urgencia: e.target.value })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="encargos-ia-filtro-urgencia">
            <option value="">Cualquier urgencia</option>
            <option value="inmediata">Inmediata</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
          <select value={filtros.orden} onChange={(e) => cambiar({ orden: e.target.value })} className="border border-gray-200 rounded-md px-2 py-1.5" data-testid="encargos-ia-filtro-orden">
            <option value="urgencia">Por urgencia</option>
            <option value="recientes">Más recientes</option>
            <option value="expediente">Por nº de expediente</option>
          </select>
          <label className="flex items-center gap-2 text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filtros.solo_letrado} onChange={(e) => cambiar({ solo_letrado: e.target.checked })} className="w-4 h-4 accent-blue-600" data-testid="encargos-ia-filtro-letrado" />
            Solo los que decide el letrado
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {['propuesto', 'aprobado', 'hecho', 'descartado', 'todos'].map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => cambiar({ estado: e })}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors duration-150 ${filtros.estado === e ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}
            data-testid={`encargos-ia-estado-${e}`}
          >
            {e === 'todos' ? 'Todos' : ESTADOS[e].etq}
            {e !== 'todos' && <span className="ml-2 text-xs opacity-70">{contadores[e] || 0}</span>}
          </button>
        ))}
      </div>

      {aviso && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm mb-4" data-testid="encargos-ia-aviso">{aviso}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm mb-4" data-testid="encargos-ia-error">{error}</div>}

      <div className="flex items-center justify-between gap-3 flex-wrap mb-3 text-sm text-gray-600">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={todos}
            onChange={() => setSeleccion(todos ? [] : resultados.map(clave))}
            className="w-4 h-4 accent-blue-600"
            data-testid="encargos-ia-seleccionar-todos"
          />
          {seleccion.length > 0 ? `${seleccion.length} seleccionados` : 'Seleccionar la página'}
        </label>
        <span data-testid="encargos-ia-total">{total} encargo{total === 1 ? '' : 's'} · página {filtros.pagina} de {paginas}</span>
      </div>

      {seleccion.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="encargos-ia-acciones-lote">
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => decidir(seleccion, 'aprobado')} data-testid="encargos-ia-lote-aprobar">
            <Check className="w-4 h-4 mr-2" />Aprobar
          </Button>
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => decidir(seleccion, 'hecho')} data-testid="encargos-ia-lote-hecho">
            <CheckCheck className="w-4 h-4 mr-2" />Dar por hecho
          </Button>
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => decidir(seleccion, 'descartado')} data-testid="encargos-ia-lote-descartar">
            <Ban className="w-4 h-4 mr-2" />Descartar
          </Button>
        </div>
      )}

      {cargando && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />Cargando encargos…
        </div>
      )}

      {!cargando && resultados.length === 0 && (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center" data-testid="encargos-ia-vacio">
          <ClipboardList className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No hay encargos con estos filtros</p>
          <p className="text-sm text-gray-400 mt-1">Los encargos aparecen cuando el auditor los propone en una auditoría.</p>
        </div>
      )}

      {!cargando && resultados.length > 0 && (
        <div className="space-y-3" data-testid="encargos-ia-listado">
          {resultados.map((item) => (
            <FilaEncargo
              key={clave(item)}
              item={item}
              seleccionado={seleccion.includes(clave(item))}
              onSeleccionar={(k) => setSeleccion((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]))}
              onDecidir={decidir}
              ocupado={ocupado}
            />
          ))}
        </div>
      )}

      {paginas > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6" data-testid="encargos-ia-paginacion">
          <Button variant="outline" size="sm" disabled={filtros.pagina <= 1 || cargando}
            onClick={() => setFiltros((f) => ({ ...f, pagina: f.pagina - 1 }))} data-testid="encargos-ia-pagina-anterior">
            <ChevronLeft className="w-4 h-4 mr-1" />Anterior
          </Button>
          <span className="text-sm text-gray-600">{filtros.pagina} / {paginas}</span>
          <Button variant="outline" size="sm" disabled={filtros.pagina >= paginas || cargando}
            onClick={() => setFiltros((f) => ({ ...f, pagina: f.pagina + 1 }))} data-testid="encargos-ia-pagina-siguiente">
            Siguiente<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default EncargosIAPanel;


// ───────────────── Borradores del agente: revisarlos o borrarlos ─────────────────
export const BorradoresIAPanel = () => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [texto, setTexto] = useState('');
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(1);
  const [seleccion, setSeleccion] = useState([]);
  const temporizador = useRef(null);

  const cargar = useCallback(async (busqueda, pag) => {
    setCargando(true);
    setError('');
    try {
      const res = await expedientesAPI.auditoriaIABorradores({ q: busqueda, pagina: pag, por_pagina: 50 });
      setDatos(res.data);
    } catch (e) {
      setError('No se han podido cargar los borradores.');
    }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(q, pagina); }, [cargar, q, pagina]);
  useEffect(() => {
    temporizador.current && clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => { setPagina(1); setQ(texto); }, 400);
    return () => temporizador.current && clearTimeout(temporizador.current);
  }, [texto]);

  const resultados = datos?.resultados || [];
  const total = datos?.total || 0;
  const paginas = datos?.paginas || 1;

  const borrar = async (ids) => {
    if (!window.confirm(`Se borrarán ${ids.length} borrador(es) del expediente, con su Word adjunto. No se puede deshacer. ¿Continuar?`)) return;
    setOcupado(true);
    setAviso('');
    try {
      const res = await expedientesAPI.auditoriaIABorrarBorradores(ids);
      setAviso(`Borrados ${res.data?.borradas || 0} borrador(es).`);
      setSeleccion([]);
      await cargar(q, 1);
      setPagina(1);
    } catch (e) {
      setError('No se han podido borrar.');
    }
    setOcupado(false);
  };

  return (
    <div data-testid="borradores-ia-panel">
      <p className="text-sm text-gray-600 mb-4">
        Escritos y correos que ha preparado el agente. Quédate con los que vayas a usar y borra el resto;
        el Word adjunto se borra con ellos.
      </p>
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar por nº de expediente, cliente o tipo de escrito…"
          className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="borradores-ia-buscador"
        />
      </div>
      {aviso && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm mb-4" data-testid="borradores-ia-aviso">{aviso}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm mb-4" data-testid="borradores-ia-error">{error}</div>}

      <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={resultados.length > 0 && seleccion.length === resultados.length}
            onChange={(e) => setSeleccion(e.target.checked ? resultados.map((r) => r.id) : [])}
            className="w-4 h-4 accent-blue-600"
            data-testid="borradores-ia-seleccionar-todos"
          />
          Seleccionar la página
        </label>
        <span data-testid="borradores-ia-total">{total} borrador{total === 1 ? '' : 'es'} · página {pagina} de {paginas}</span>
      </div>

      {seleccion.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="borradores-ia-acciones-lote">
          <span className="text-sm text-red-900 font-medium">{seleccion.length} seleccionado(s)</span>
          <Button variant="outline" size="sm" disabled={ocupado} onClick={() => borrar(seleccion)} data-testid="borradores-ia-lote-borrar">
            <Trash2 className="w-4 h-4 mr-2" />Borrar seleccionados
          </Button>
        </div>
      )}

      {cargando ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
      ) : resultados.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center" data-testid="borradores-ia-vacio">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No hay borradores del agente.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="borradores-ia-listado">
          {resultados.map((b) => (
            <div key={b.id} className="bg-white rounded-lg border border-gray-200 p-3 flex items-start gap-3" data-testid={`borrador-ia-fila-${b.id}`}>
              <input
                type="checkbox"
                checked={seleccion.includes(b.id)}
                onChange={() => setSeleccion((s) => (s.includes(b.id) ? s.filter((x) => x !== b.id) : [...s, b.id]))}
                className="mt-1 w-4 h-4 accent-blue-600 cursor-pointer"
                aria-label={`Seleccionar borrador de ${b.numero_expediente}`}
                data-testid={`borrador-ia-seleccionar-${b.id}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <a href={`/expedientes/${b.expediente_id}`} target="_blank" rel="noopener noreferrer"
                    className="font-semibold text-blue-700 hover:text-blue-900 hover:underline flex items-center gap-1"
                    data-testid={`borrador-ia-ir-expediente-${b.id}`}>
                    {b.numero_expediente}<ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {b.con_word && (
                    <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" />Word adjunto
                    </span>
                  )}
                  {b.asistido && (
                    <span className="text-[11px] text-orange-700 bg-orange-100 rounded-full px-2 py-0.5">asistido</span>
                  )}
                </div>
                <p className="text-sm text-gray-900 mt-1">{b.titulo}</p>
                <p className="text-xs text-gray-500 mt-0.5">{fechaCorta(b.fecha)}{b.cliente ? ` · ${b.cliente}` : ''}</p>
              </div>
              <button type="button" disabled={ocupado} onClick={() => borrar([b.id])}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50"
                data-testid={`borrador-ia-borrar-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5" />Borrar
              </button>
            </div>
          ))}
        </div>
      )}

      {paginas > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6" data-testid="borradores-ia-paginacion">
          <Button variant="outline" size="sm" disabled={pagina <= 1 || cargando} onClick={() => setPagina((p) => p - 1)} data-testid="borradores-ia-pagina-anterior">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-gray-600">{pagina} / {paginas}</span>
          <Button variant="outline" size="sm" disabled={pagina >= paginas || cargando} onClick={() => setPagina((p) => p + 1)} data-testid="borradores-ia-pagina-siguiente">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
